import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PackageCheck, PackageX, Split, Printer } from "lucide-react";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { receiveOrder } from "../../api/warehouse";
import { openOfficialDoc } from "../../api/docPrint";
import { ReceiveLinesGrid } from "./ReceiveLinesGrid";

const MODES = [
  { key: "full", icon: PackageCheck, color: "emerald" },
  { key: "shortage", icon: PackageX, color: "amber" },
  { key: "partial", icon: Split, color: "sky" },
];
const FIELDS = ["waybill_no", "driver_name", "vehicle_number", "driver_phone"];

// نموذج استلام المخزن: 3 أوضاع (مطابق/ نقص/ مجزأ) + طباعة نسختين تلقائياً.
export const WarehouseReceiveForm = ({ detail, onClose, onDone }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("full");
  const [form, setForm] = useState({ waybill_no: "", driver_name: "", vehicle_number: "", driver_phone: "" });
  const initial = {}; (detail.lines || []).forEach((l) => { initial[l.line_no] = l.remaining_quantity; });
  const [qtys, setQtys] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const missing = FIELDS.filter((k) => !(form[k] || "").trim());

  const receive = async () => {
    if (missing.length) {
      setTouched(true);
      toast.error(t("warehouse.fields.required"));
      return;
    }
    setBusy(true);
    try {
      const res = await receiveOrder(detail.id, {
        reception_type: mode, date: new Date().toISOString().slice(0, 10),
        waybill_no: form.waybill_no || null,
        driver: { driver_name: form.driver_name || null, vehicle_number: form.vehicle_number || null,
          driver_phone: form.driver_phone || null },
        quantities: Object.entries(qtys).map(([n, q]) => ({ line_no: Number(n), quantity: String(q || 0) })),
      });
      toast.success(t("warehouse.received"));
      await openOfficialDoc("receipt", res.receipt.id).catch(() => null);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || t("errors.generic")); }
    finally { setBusy(false); }
  };

  return (
    <NeuCard className="grid gap-4 p-5" data-testid="wh-receive-form">
      <div className="flex items-center justify-between">
        <p className="text-lg font-black">{t("warehouse.receiving", { number: detail.number })}</p>
        <NeuButton onClick={onClose}>{t("app.back")}</NeuButton>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {MODES.map(({ key, icon: Icon, color }) => (
          <button key={key} type="button" data-testid={`wh-mode-${key}`} onClick={() => setMode(key)}
                  className={`flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold transition ${
                    mode === key ? `bg-${color}-500/15 text-${color}-700 shadow-neu-in` : "shadow-neu-sm"}`}>
            <Icon className="h-4 w-4" /> {t(`warehouse.mode.${key}`)}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        {FIELDS.map((k) => (
          <NeuInput key={k} data-testid={`wh-${k}`} value={form[k]} required
                    placeholder={`${t(`warehouse.fields.${k}`)} *`}
                    className={touched && !form[k].trim() ? "ring-2 ring-rose-500" : ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
        ))}
      </div>
      {touched && missing.length > 0 && (
        <p className="text-xs font-bold text-rose-600" data-testid="wh-fields-error">
          ⚠️ {t("warehouse.fields.required")}
        </p>
      )}
      <div className="rounded-2xl bg-amber-500/10 px-4 py-2 text-xs font-bold text-amber-700"
           data-testid="wh-ref-header">
        {t("warehouse.doc.reference")}: <span className="num font-mono">{detail.number}</span>
        {detail.ref_number && <span className="num font-mono"> · {detail.ref_number}</span>}
      </div>
      <ReceiveLinesGrid lines={detail.lines} qtys={qtys} setQtys={setQtys}
                        disabled={mode === "full"} />
      <div className="flex gap-2 border-t border-border/40 pt-4">
        <NeuButton variant="primary" data-testid="wh-submit" disabled={busy} onClick={receive}>
          <Printer className="inline h-4 w-4" /> {t("warehouse.receiveAndPrint")}
        </NeuButton>
        <NeuButton onClick={onClose}>{t("app.cancel")}</NeuButton>
      </div>
      <p className="text-[11px] text-muted-foreground">💡 {t("warehouse.twoCopiesHint")}</p>
    </NeuCard>
  );
};
